import * as React from 'react'

import {
  AllProductUpdatesDocument,
  GetMarketplaceIntegrationsDocument,
  OnboardingTabsDocument,
  UpsellLandingPagesDocument,
} from 'generated/resources/graphql'

import i18n from 'modules/core/lib/i18n'
import { NavigationRoutesFn } from 'modules/core/lib/react-router'

import LandingPage from 'modules/discover/pages/LandingPage'
import { ItemHeader } from 'modules/marketplace/components/Item/ItemHeader'
import { addFromParameter } from 'modules/marketplace_modules/hooks/useMarketplaceModulesPath'
import { getMarketplaceModulesRoute } from 'modules/marketplace_modules/routes'
import { getDatoLocale } from 'modules/onboarding/lib/datoLocale'

const RootPage = React.lazy(() => import('../pages/RootPage'))

const AutoOnboarding = React.lazy(
  () => import('modules/product_updates/pages/ProductUpdateAutoOnboarding')
)
const ProductUpdateDetailPage = React.lazy(
  () => import('modules/product_updates/pages/ProductUpdateDetailPage')
)
const ProductUpdatesPage = React.lazy(
  () => import('modules/product_updates/pages/ProductUpdatesPage')
)

const MarketplaceRootPage = React.lazy(() => import('modules/marketplace/pages/RootPage'))
const ForbiddenPage = React.lazy(() => import('modules/marketplace/pages/ForbiddenPage'))
const IntegrationSettingsPage = React.lazy(
  () => import('modules/marketplace/pages/IntegrationSettingsPage')
)
const ItemPage = React.lazy(() => import('modules/marketplace/pages/ItemPage'))
const ListIntegrationsPage = React.lazy(
  () => import('modules/marketplace/pages/ListIntegrationsPage')
)
const NotEnabledPage = React.lazy(() => import('modules/marketplace/pages/NotEnabledPage'))

const UpdatesWithCEO = React.lazy(() => import('modules/product_updates/pages/UpdatesWithCEOPage'))

const LaunchesWithCEOPage = React.lazy(
  () => import('modules/product_updates/pages/LaunchesWithCEOPage')
)

const DiscoverGuardPage = React.lazy(() => import('../pages/DiscoverGuardPage'))

const OneLandingPage = React.lazy(() => import('modules/upsell/pages/OneLandingPage'))
const OnePromoPage = React.lazy(() => import('modules/upsell/pages/OnePromoPage'))

export const discoverRoutes: NavigationRoutesFn = (navigation) => {
  return [
    {
      path: navigation.discover.path,
      element: <RootPage />,
      handle: {
        module: 'discover',
        meta: ({ watchQuery, location }) => {
          return watchQuery({
            query: OnboardingTabsDocument,
          }).map(({ data }) => {
            return {
              title: i18n.t('sidebar.product_updates.discover'),

              subNavigation: [
                {
                  title: i18n.t('product_updates.onboarding'),
                  href: navigation.discover.autoOnboarding.fullPath(),
                  isVisible: data.trial._policies.companyOnboardingShowCompanyOnboarding.can,
                },
                {
                  title: i18n.t('sidebar.product_updates.news'),
                  href: navigation.discover.productUpdates.fullPath(),
                  isVisible: data.productUpdates._policies.sidebarSeeProductUpdatesSubLevelItem.can,
                },
                {
                  title: i18n.t('sidebar.modules'),
                  href: addFromParameter(
                    navigation.discover.marketplaceModules.fullPath(),
                    location.pathname
                  ),
                  isVisible: data.modules._policies.sidebarSeeModulesSubLevelItem.can,
                },
                {
                  title: i18n.t('sidebar.integrations'),
                  href: navigation.discover.marketplace.fullPath(),
                  isVisible: data.marketplace._policies.sidebarSeeMarketplaceSubLevelItem.can,
                },
              ],
            }
          })
        },
      },

      children: [
        {
          index: true,
          element: <DiscoverGuardPage />,
        },
        // ProductUpdates Module
        {
          path: navigation.discover.autoOnboarding.fullPath(),
          element: <AutoOnboarding />,
          handle: {
            meta: () => ({
              title: 'Onboarding',
            }),
          },
        },
        {
          path: navigation.discover.productUpdates.fullPath(),
          children: [
            {
              index: true,
              element: <ProductUpdatesPage />,
              handle: {
                meta: () => ({
                  title: i18n.t('sidebar.product_updates.news'),
                  illustration: 'multiple-buildings',
                }),
              },
            },
            {
              path: navigation.discover.productUpdates.item.path,
              element: <ProductUpdateDetailPage />,
              handle: {
                resource: true,
                meta: ({
                  watchQuery,
                  params: { slug },
                  session: {
                    access: { locale },
                  },
                }) => {
                  const datoLocale = getDatoLocale(locale)
                  return watchQuery({
                    query: AllProductUpdatesDocument,
                    variables: {
                      locale: datoLocale,
                      preview: false,
                      slug,
                    },
                    fetchPolicy: 'cache-first',
                  }).map(({ data }) => {
                    const title = data.productUpdates.updatesConnection.edges[0]?.node.title || ''
                    return { title }
                  })
                },
              },
            },
          ],
        },
        getMarketplaceModulesRoute(navigation),
        {
          path: navigation.discover.marketplace.path,
          handle: {
            responsive: true,
            meta: () => ({
              title: i18n.t('marketplace.integrations.title'),
            }),
          },
          element: <MarketplaceRootPage />,
          children: [
            {
              index: true,
              element: <ListIntegrationsPage />,
            },
            {
              path: navigation.discover.marketplace.item.path,
              children: [
                {
                  element: <MarketplaceRootPage />,
                  handle: {
                    meta: ({ params, watchQuery }) => {
                      return watchQuery({
                        query: GetMarketplaceIntegrationsDocument,
                        variables: {
                          datoIds: [params['id'] || ''],
                        },
                      }).map(({ data }) => {
                        const integration = data?.marketplace.integrations[0]
                        return {
                          title:
                            integration?.name || i18n.t('marketplace.integrations.integration'),
                        }
                      })
                    },
                  },
                  children: [
                    {
                      index: true,
                      element: <ItemPage />,
                      handle: {
                        resource: true,
                        meta: ({ params, watchQuery }) => {
                          return watchQuery({
                            query: GetMarketplaceIntegrationsDocument,
                            variables: {
                              datoIds: [params['id'] || ''],
                            },
                          }).map(({ data }) => {
                            const integration = data?.marketplace.integrations[0]
                            return {
                              title:
                                integration?.name || i18n.t('marketplace.integrations.integration'),
                              headerComponent: integration && (
                                <ItemHeader integration={integration} />
                              ),
                            }
                          })
                        },
                      },
                    },
                    {
                      path: navigation.discover.marketplace.item.settings.path,
                      element: <IntegrationSettingsPage />,
                      handle: {
                        resource: true,
                        meta: () => {
                          return {
                            title: i18n.t('marketplace.integrations.settings'),
                          }
                        },
                      },
                    },
                  ],
                },
              ],
            },
            {
              path: navigation.discover.marketplace.not_enabled.path,
              element: <NotEnabledPage />,
            },
            {
              path: navigation.discover.marketplace.forbidden_access.path,
              element: <ForbiddenPage />,
            },
          ],
        },
        // UpdatesWithCEO
        {
          path: navigation.discover.updatesWithCEO.fullPath(),
          element: <UpdatesWithCEO />,
          handle: {
            resource: true,
            meta: ({
              watchQuery,
              session: {
                access: { locale },
              },
            }) => {
              const datoLocale = getDatoLocale(locale)

              return watchQuery({
                query: AllProductUpdatesDocument,
                variables: {
                  locale: datoLocale,
                  preview: false,
                },
                fetchPolicy: 'cache-first',
              }).map(() => {
                return {
                  title: i18n.t('product_updates.updates_with_ceo.title'),
                  datoLocale,
                }
              })
            },
          },
        },
        {
          path: navigation.discover.launchesWithCEO.fullPath(),
          element: <LaunchesWithCEOPage />,
          handle: {
            resource: true,
            meta: ({
              watchQuery,
              session: {
                access: { locale },
              },
            }) => {
              const datoLocale = getDatoLocale(locale)

              return watchQuery({
                query: AllProductUpdatesDocument,
                variables: {
                  locale: datoLocale,
                  preview: false,
                },
                fetchPolicy: 'cache-first',
              }).map(() => {
                return {
                  title: i18n.t('product_updates.launches_with_ceo.title'),
                  datoLocale,
                }
              })
            },
          },
        },
        {
          path: navigation.discover.oneLandingPage.fullPath(),
          element: <OneLandingPage />,
          handle: {
            resource: true,
            meta: () => {
              return {
                title: i18n.t('growth.one_waitlist.title'),
              }
            },
          },
        },
        {
          path: navigation.discover.onePromoPage.fullPath(),
          element: <OnePromoPage />,
          handle: {
            resource: true,
            meta: () => {
              return {
                title: i18n.t('growth.one_waitlist.title'),
              }
            },
          },
        },
        {
          path: navigation.discover.landing.fullPath(),
          children: [
            {
              path: navigation.discover.landing.item.path,
              element: <LandingPage />,
              handle: {
                meta: ({
                  params,
                  watchQuery,
                  session: {
                    access: { locale },
                  },
                }) => {
                  const datoLocale = getDatoLocale(locale)

                  return watchQuery({
                    query: UpsellLandingPagesDocument,
                    variables: {
                      locale: datoLocale,
                      preview: params['preview'] === 'true',
                      slug: params['id'] || '',
                    },
                  }).map(({ data }) => {
                    return {
                      title: data?.upsell.landingPagesConnection.edges[0]?.node.title || '',
                    }
                  })
                },
              },
            },
          ],
        },
      ],
    },
  ]
}
